/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { Autocomplete } from '@base-ui/react/autocomplete';
import { Dialog } from '@base-ui/react/dialog';
import * as React from 'react';

import { Icon, Spinner } from 'sarsaparilla';

import { useAuthState } from 'shared-ui/auth';

import { navigateToSuggestion, startsNewSearch } from './QuickSearch.navigation';
import {
    useClearSearchHistoryMutation,
    useSearchHistoryQuery,
    useSearchSuggestionsQuery,
} from './QuickSearch.queries';
import { createEmptyStateGroups, prependQuerySuggestion } from './QuickSearch.transforms';
import type { SearchSuggestionItem } from './QuickSearch.types';
import { SuggestionList } from './SuggestionList';

import styles from './QuickSearch.module.scss';

export function QuickSearch() {
    const [isOpen, setIsOpen] = React.useState(false);
    const [inputValue, setInputValue] = React.useState<string>('');
    const [highlightedItem, setHighlightedItem] = React.useState<
        SearchSuggestionItem | undefined
    >(undefined);

    const shortcutsDescriptionId = React.useId();

    const { account } = useAuthState();
    const { data, isFetching, isError } = useSearchSuggestionsQuery(inputValue.trim());
    const { data: history } = useSearchHistoryQuery({
        accountId: account?.account_id,
        enabled: isOpen,
    });
    const clearHistory = useClearSearchHistoryMutation();

    React.useEffect(() => {
        function handleKeyDown(event: KeyboardEvent) {
            if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
                // Also keeps the browser from focusing its own search bar on Ctrl+K
                event.preventDefault();
                setIsOpen(true);
            }
        }

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, []);

    const items = React.useMemo(() => {
        if (!inputValue.trim()) {
            return createEmptyStateGroups(history);
        }

        return prependQuerySuggestion(data ?? [], inputValue);
    }, [data, history, inputValue]);

    function handleItemClick(item: SearchSuggestionItem) {
        if (item.type === 'clear-history') {
            clearHistory.mutate();
            return;
        }

        setIsOpen(false);
        setInputValue('');
        navigateToSuggestion(item);
    }

    return (
        <Dialog.Root
            open={isOpen}
            onOpenChange={(nextOpen) => {
                if (!nextOpen) {
                    setInputValue('');
                    clearHistory.reset();
                }
                setIsOpen(nextOpen);
            }}
        >
            <Dialog.Trigger
                className={styles.Button}
                aria-label="Search"
                aria-keyshortcuts="Meta+K Control+K"
            >
                <MagnifyingGlassIcon strokeWidth={2.5} />
                Search ...
                {/* <kbd className={styles.NakedKbd}>&#x2318;K</kbd> */}
            </Dialog.Trigger>
            <Dialog.Portal>
                <Dialog.Backdrop className={styles.Backdrop} />
                <Dialog.Viewport className={styles.Viewport}>
                    <Dialog.Popup className={styles.Popup} aria-label="Command palette">
                        <Autocomplete.Root
                            open
                            inline
                            mode="none"
                            items={items}
                            autoHighlight="always"
                            keepHighlight
                            value={inputValue}
                            onValueChange={setInputValue}
                            onItemHighlighted={setHighlightedItem}
                        >
                            <Autocomplete.InputGroup className={styles.InputGroup}>
                                <MagnifyingGlassIcon
                                    className={styles.InputIcon}
                                    aria-hidden
                                />
                                <Autocomplete.Input
                                    className={styles.Input}
                                    aria-label="Search commands"
                                    aria-describedby={shortcutsDescriptionId}
                                    placeholder="Search …"
                                />

                                {isFetching ? (
                                    <div
                                        className={styles.SpinnerContainer}
                                        aria-hidden="true"
                                    >
                                        <Spinner size="xs" />
                                    </div>
                                ) : null}
                            </Autocomplete.InputGroup>
                            <Dialog.Close
                                className={styles.SkipLink}
                                aria-label="Close search"
                            >
                                <Icon iconName="close" size="sm" />
                            </Dialog.Close>

                            <div className={styles.ListArea}>
                                <div className={styles.ListViewport}>
                                    <div className={styles.ListContent}>
                                        <Autocomplete.Empty>
                                            <div className={styles.Empty}>
                                                No results found
                                            </div>
                                        </Autocomplete.Empty>

                                        <SuggestionList onItemClick={handleItemClick} />

                                        <Autocomplete.Status>
                                            {isFetching ? (
                                                <div className="rec-sr-only">
                                                    Loading suggestions
                                                </div>
                                            ) : isError ? (
                                                <div className={styles.errorStatus}>
                                                    <Icon iconName="warning" />
                                                    Sorry, suggestions aren’t loading
                                                    right now
                                                </div>
                                            ) : clearHistory.isSuccess &&
                                              !inputValue.trim() ? (
                                                <div className="rec-sr-only">
                                                    Search history cleared
                                                </div>
                                            ) : null}
                                        </Autocomplete.Status>
                                    </div>
                                </div>
                            </div>

                            <div className={styles.Footer}>
                                <span id={shortcutsDescriptionId} className="rec-sr-only">
                                    Use Enter to activate the highlighted item.
                                </span>
                                <div className={styles.FooterLeft}>
                                    <kbd className={styles.Kbd}>Enter</kbd>
                                    <span>{getEnterHint(highlightedItem)}</span>
                                </div>
                                <div className={styles.FooterRight}>
                                    <kbd className={styles.Kbd}>Esc</kbd>
                                    <span>Close</span>
                                </div>
                            </div>
                        </Autocomplete.Root>
                    </Dialog.Popup>
                </Dialog.Viewport>
            </Dialog.Portal>
        </Dialog.Root>
    );
}

function getEnterHint(item: SearchSuggestionItem | undefined) {
    if (item?.type === 'clear-history') {
        return 'Clear search history';
    }

    return item && startsNewSearch(item) ? 'Start a new map search' : 'Navigate';
}

function MagnifyingGlassIcon(props: React.ComponentProps<'svg'>) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            {...props}
            style={{ ...props.style }}
        >
            <path d="m21 21-4.34-4.34" />
            <circle cx="11" cy="11" r="8" />
        </svg>
    );
}
